/**
 * Execution lifecycle helpers for the recovery-resilience model.
 *
 * The API can now report an execution as `suspended` (a transient-infra recovery defer that
 * auto-resumes) alongside starting/running/recovering/stopped/crashed/halted, plus a `desired_state`
 * ("running"/"stopped") the operator controls. These helpers centralize the "is there a live worker"
 * and "does this still count as active" questions so lists, badges, and command gating stay in sync.
 */

/**
 * The subset of execution fields these helpers reason about. Kept structural (not `ApiExecution`) so
 * list rows, group members, and the run-detail object can all be passed without a cast.
 */
export interface ExecutionLifecycleFields {
  end_ts?: string | null;
  status_name?: string | null;
  desired_state?: "running" | "stopped" | null;
  suspend_cause_name?: "infra" | "safety" | null;
}

// Statuses in which a worker holds a live lease and can consume worker-directed commands (End,
// Recovery, capital/withdraw/order commands). Outside this set there is no worker to act on those
// commands, so the UI must offer operator actions (resume/stop) instead of the command menu.
const LIVE_WORKER_STATUSES = new Set(["running", "recovering", "starting"]);

// Statuses that keep a non-ended execution "active" for list/grouping visibility. `suspended`
// (auto-resuming infra defer) and `recovering` MUST be here so an auto-suspended execution never
// silently drops out of an "active only" view.
const ACTIVE_STATUSES = new Set(["running", "recovering", "starting", "suspended"]);

/**
 * True when a live worker holds the lease, so worker-directed commands make sense. Legacy rows with
 * no status_name (predating the resume system) are treated as live to preserve the prior behavior of
 * showing commands for any non-ended execution.
 */
export const hasLiveWorker = (execution: ExecutionLifecycleFields): boolean => {
  if (execution.end_ts) return false;
  const status = execution.status_name;
  if (!status) return true;
  return LIVE_WORKER_STATUSES.has(status);
};

/**
 * True when an execution should count as active in lists/groups. Any non-ended row with a live or
 * transitional status (including suspended/recovering); legacy no-status rows count as active.
 */
export const isExecutionActive = (execution: ExecutionLifecycleFields): boolean => {
  if (execution.end_ts) return false;
  const status = execution.status_name;
  if (!status) return true;
  return ACTIVE_STATUSES.has(status);
};

/**
 * True when the operator resume-desired action applies: a stranded stopped `crashed` row
 * (desired_state 'stopped'), still open (no end_ts). Mirrors the backend CAS eligibility so the button
 * only shows when the server will accept it. NOT halted: a halted+stopped row is an open-close_market
 * bookkeeping-halt with unverified exposure and must go through the repair path, never a plain resume.
 */
export const canResumeDesired = (execution: ExecutionLifecycleFields): boolean =>
  !execution.end_ts && execution.desired_state === "stopped" && execution.status_name === "crashed";

/**
 * True when a safety halt is in force: `suspend_cause_name === "safety"`, whatever the status reads.
 *
 * The backend writes that cause when a recovery attempt decided the execution is unsafe to resume.
 * Several backend lifecycle writes then refuse the row on that cause alone — deleting the execution
 * among them — so the exits an ordinary halt has are withheld until an operator signs it off.
 * POST /executions/{id}/recover clears the cause deliberately and puts the execution back in the
 * orchestrator's hands rather than closing its books; it refuses a row whose desired_state is
 * 'stopped', so a safety cause does not on its own mean Recover is available. The desired-stopped
 * shape has its own exit instead — POST /executions/{id}/resolve-safety-stop, see
 * canResolveSafetyStop — which finalizes rather than resumes.
 *
 * Checked on the cause alone rather than on status+cause: the cause is the field that carries the
 * meaning.
 */
export const isSafetyHalted = (execution: ExecutionLifecycleFields): boolean =>
  execution.suspend_cause_name === "safety";

/**
 * True when the operator repair-stop action applies: a bookkeeping-halt reached on a user stop
 * (status 'halted', desired_state 'stopped', no end_ts) that carries no safety cause. The exposure could
 * not be verified flat when the stop drained with no live worker, so the row is parked instead of ended.
 * Tracks the backend POST /executions/{id}/repair-stop admission on the four fields that CAS reads from
 * the row (end_ts, desired_state, status_name, suspend_cause_name); the CAS also requires no live lease,
 * which is not in this shape, so a row this returns true for can still be refused.
 * Distinct from canResumeDesired (a stranded 'crashed' row) and canForceRecover (a desired-running row).
 */
export const canRepairHaltedStop = (execution: ExecutionLifecycleFields): boolean =>
  !execution.end_ts &&
  execution.desired_state === "stopped" &&
  execution.status_name === "halted" &&
  !isSafetyHalted(execution);

/**
 * True when the operator safety-stop resolution applies: an open safety halt (status 'halted', a
 * safety cause, no end_ts) whose operator has set desired_state 'stopped'.
 *
 * That combination is refused by the four transitions beside it — recover rejects desired_state 'stopped';
 * repair-stop, request-stop and the no-worker terminalizer exclude the safety cause; resume-desired
 * requires 'crashed' — so it is the shape POST /executions/{id}/resolve-safety-stop exists for.
 * Tracks that CAS's admission on the four fields it reads from the row (suspend_cause_name,
 * status_name, desired_state, end_ts); the CAS also requires no live lease, which is not in this
 * shape, so a row this returns true for can still be refused.
 */
export const canResolveSafetyStop = (execution: ExecutionLifecycleFields): boolean =>
  !execution.end_ts &&
  execution.desired_state === "stopped" &&
  execution.status_name === "halted" &&
  isSafetyHalted(execution);

/**
 * True when the operator force-recover action applies: a non-ended execution the operator still wants
 * alive (desired_state 'running') whose worker is gone, wedged or halted — status crashed, recovering,
 * halted, or a stale 'running' lease. The backend POST /executions/{id}/recover refuses three things,
 * and this returns false for each of them: a finalized row (end_ts set), a user-stopped row
 * (desired_state 'stopped') and a 'stopped' status. This is the narrower predicate of the two: the
 * server also accepts 'starting' and 'suspended', which the button does not offer.
 *
 * 'halted' is included for the two halted+desired=running shapes. A worker that hit an unreconcilable
 * exchange/DB mismatch halts itself with no suspend cause; a recovery attempt that judged the execution
 * unsafe to resume halts it with suspend_cause_name 'safety'. Recover is the documented exit for both,
 * and on the safety one the sibling predicates withhold both Stop (refused for the cause) and Repair
 * Stop (which requires desired_state 'stopped', and this shape wants 'running'). Neither shape is a bookkeeping-halt: those carry
 * desired_state 'stopped' and are excluded here, because closing their books goes through Repair Stop.
 *
 * Distinct from canResumeDesired: recover forces the orchestrator to re-claim a still-desired-running
 * row; resume flips a stranded desired-stopped one back to running.
 */
export const canForceRecover = (execution: ExecutionLifecycleFields): boolean =>
  !execution.end_ts &&
  execution.desired_state === "running" &&
  (execution.status_name === "crashed" ||
    execution.status_name === "recovering" ||
    execution.status_name === "halted" ||
    execution.status_name === "running");

export interface ExecutionStatusMeta {
  color: string; // CoreUI CBadge color
  label: string;
}

const STATUS_META: Record<string, ExecutionStatusMeta> = {
  running: { color: "success", label: "Running" },
  recovering: { color: "warning", label: "Recovering" },
  starting: { color: "info", label: "Starting" },
  suspended: { color: "warning", label: "Suspended" },
  crashed: { color: "danger", label: "Crashed" },
  halted: { color: "danger", label: "Halted" },
  stopped: { color: "secondary", label: "Stopped" },
};

/** Badge color + human label for an execution status. Unknown/missing -> a neutral fallback. */
export const getExecutionStatusMeta = (status: string | null | undefined): ExecutionStatusMeta =>
  (status ? STATUS_META[status] : undefined) ?? { color: "secondary", label: status || "unknown" };
